import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import classNames from 'classnames';
import { Icon } from 'ui';
import { IconTypes } from 'constants/iconTypes';
import DateFormat from 'constants/date_format';
import { SavedElementTypes } from 'constants/savedElements';

class ElementLibraryItem extends Component {
  /**
   * Determines if the element has linked elements support. That is, the ability
   * to edit the element in the Element Builder.
   */
  isEditable() {
    const { isEnabledLinkedElement, canManageContentLibrary } = this.props;
    return canManageContentLibrary && isEnabledLinkedElement;
  }

  /**
   * The element can be removed directly from the modal:
   *
   * if   it does NOT have linked elements support. Meaning it cannot be edited
   *      in the element builder. This is a simple feature flag check.
   *
   * or,  the element IS LINKED AND the feature flag for linked elements is
   *      disabled. This means that the feature flag was turned off after the
   *      element has been created (edge case).
   *
   * or,  the element is NOT created by the current user
   */
  isRemovable() {
    const { item, currentUserId } = this.props;

    if (this.isEditable()) {
      return false;
    }

    if (item.saved_element_type === SavedElementTypes.Linked) {
      return false;
    }

    return currentUserId === item.creator_id;
  }

  handleDelete = () => {
    this.props.deleteItem(this.props.item);
  };

  render() {
    const { item, active } = this.props;

    const elementLibraryItemClassName = classNames('ElementLibraryItem', {
      'ElementLibraryItem--active': active,
    });

    const itemTitleClassName = classNames(
      'ElementLibraryItem-title',
      {
        'ElementLibraryItem--active': active,
      },
      'text-wrap',
    );

    const itemDescriptionClassName = classNames(
      'ElementLibraryItem-description',
      {
        'ElementLibraryItem--active': active,
      },
      'text-wrap',
    );

    const infoClassName = classNames('ElementLibraryItem-info', {
      'd-f ai-c': !active,
    });

    const iconName = IconTypes[item.display_type];

    return (
      <div
        onClick={() => this.props.onClick(this.props.item)}
        onKeyDown={e =>
          e.key === 'Enter' && this.props.onClick(this.props.item)
        }
        className={elementLibraryItemClassName}
      >
        <div className={infoClassName}>
          {iconName && !active && (
            <Icon name={iconName} className="mr-16 c-gray-light" />
          )}
          {active && (
            <div className="ElementLibraryItem-additionalInfo d-f jc-sb mb-16">
              <div className="fg-1">
                <Icon name={iconName} />
                <span className="ml-8 mr-16">{item.display_type}</span>
                <Icon name="learner" />
                <span className="ml-8 mr-16">{item.creator_name}</span>
                <Icon name="calendar_empty" />
                <span className="ml-8 mr-16">
                  {moment(
                    item.created_at,
                    DateFormat.fixed.FULL_YEAR_MONTH_DATE_TIME,
                  ).fromNow()}
                </span>
              </div>
              {this.isEditable() && (
                <a
                  title={t('common.edit')}
                  data-toggle="tooltip"
                  data-placement="left"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`/content/library/elements/${item.id}/edit`}
                >
                  <Icon name="edit" />
                </a>
              )}
              {this.isRemovable() && (
                <span
                  role="button"
                  tabIndex={0}
                  title="Remove from Library"
                  data-toggle="tooltip"
                  data-placement="left"
                  onClick={this.handleDelete}
                  onKeyDown={e => e.key === 'Enter' && this.handleDelete()}
                >
                  <Icon name="error" />
                </span>
              )}
            </div>
          )}
          <div>
            <span className={itemTitleClassName}>{item.title} &nbsp;</span>
            {item.saved_element_type === SavedElementTypes.Linked && (
              <Icon name="link" className="c-gray-darker mr-4" />
            )}
            {item.description && (
              <span className={itemDescriptionClassName}>
                &ndash; {item.description}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ meta: { currentUser } }) => ({
  canManageContentLibrary: currentUser.canManageContentLibrary,
});

const ElementLibraryItemWrapper = props => {
  const isEnabledLinkedElement = props.item.type?.toLowerCase() === 'paragraph';

  return (
    <ElementLibraryItem
      isEnabledLinkedElement={isEnabledLinkedElement}
      {...props}
    />
  );
};

export default connect(mapStateToProps)(ElementLibraryItemWrapper);
